import type {
  MarketplaceBrowseMapMarkerFragment,
  MarketplaceGeoPointInput,
} from '../browse/__generated__/marketplace-browse.zeus';
import type { MarketplaceZoneType } from './__generated__/location-search.zeus';

export type MapMarkerProperty = Omit<MarketplaceBrowseMapMarkerFragment, 'location'> & {
  isLiked: boolean;
  latitude: number;
  longitude: number;
};

/** South-west and north-east corners as `[longitude, latitude]`. */
export type MapBounds = [[number, number], [number, number]];

export type MapLocation = {
  label: string;
  zoneType: MarketplaceZoneType;
  value: string;
  coordinates?: [number, number];
  bounds?: MapBounds;
};

export type GeoPoint = MarketplaceGeoPointInput;

type SearchMapPoint = { lng: number; lat: number };

/** Viewport bounds read from whichever map engine renders the search. */
export interface SearchMapBounds {
  getNorthWest(): SearchMapPoint;
  getNorthEast(): SearchMapPoint;
  getSouthEast(): SearchMapPoint;
  getSouthWest(): SearchMapPoint;
  contains(point: [number, number]): boolean;
}

export type SearchMapCamera = {
  /** `[longitude, latitude]`. */
  center: [number, number];
  /** A Mapbox zoom level (512 px tiles); other engines convert it to their scale. Omit to keep the current zoom. */
  zoom?: number;
  duration?: number;
};

/**
 * Engine-neutral camera surface the homes page drives. Mapbox's `MapRef`
 * satisfies it structurally; other map engines provide an adapter.
 */
export interface SearchMapHandle {
  getBounds(): SearchMapBounds | null;
  isMoving(): boolean;
  stop(): void;
  flyTo(camera: SearchMapCamera, eventData?: object): void;
  jumpTo(camera: SearchMapCamera, eventData?: object): void;
  /** Fits the map to `bounds` at once, as `jumpTo` moves it. */
  fitBounds(bounds: MapBounds, options: { animate: false }, eventData?: object): void;
  once(event: 'moveend', listener: () => void): void;
  off(event: 'moveend', listener: () => void): void;
  /** The part of the map that no panel covers, in pixels. Only the Google map reports it. */
  getVisibleSize?(): { width: number; height: number };
  /**
   * The search area `getBounds` will report once the camera settles at `camera`,
   * or null when the map cannot tell. Only the Google map reports it.
   */
  getSearchAreaAt?(camera: SearchMapCamera & { zoom: number }): SearchMapBounds | null;
}

/** Fired when the camera settles. Programmatic moves can opt out of a viewport search. */
export type SearchMapMoveEvent = {
  target: Pick<SearchMapHandle, 'getBounds' | 'isMoving'>;
  odSkipViewportSearch?: boolean;
  /** Refresh pins after a property focus without replacing the browse results. */
  odPreviewAddressId?: string;
  /** The camera landed in a cluster whose search started when the zoom began. */
  odClusterZoom?: boolean;
};

export interface MarketShape {
  coordinates: GeoPoint[][];
  type: 'Polygon';
}

export interface MarketInfo {
  shape: MarketShape;
  displayName: string;
  name: string;
  identifier: string | null;
}

// Fixed heights for the map mini PDP carousel on mobile web. Pinning height
// prevents CLS while photos load; the square aspect ratio is overridden below
// the `md` breakpoint where the mobile mini PDP is shown.
export const MOBILE_CAROUSEL_HEIGHT_BASE = 260;
export const MOBILE_CAROUSEL_HEIGHT_SM = 414;

/** Static Tailwind classes — pixel values must stay in sync with MOBILE_CAROUSEL_HEIGHT_* above. */
export const MAP_MINI_PDP_CAROUSEL_LAYOUT_CLASS =
  'aspect-square w-full shrink-0 h-[260px] sm:h-[414px] md:h-auto md:aspect-video';

/** Mobile mini PDP sheet height — used for layout, closed-state slide, and dismiss animation. */
export const MOBILE_MINI_PDP_PANEL_HEIGHT = '75vh';

export const MOBILE_MINI_PDP_PANEL_HEIGHT_CLASS = 'h-[75vh]';

export const MOBILE_MINI_PDP_PANEL_CLOSED_TRANSLATE_CLASS = 'translate-y-[75vh]';
